import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { getDeliverableItemsForUser, getHandoffDeliverablesForUser, updateDeliverableItem } from '../../../api/deliverables';
import './MyDeliverables.scss';

const ROLE_COLORS = {
  Frontend: '#3b82f6',
  '整合': '#8b5cf6',
  Backend: '#f59e0b',
  QA: '#10b981',
};

const MyDeliverables = ({ onNavigate }) => {
  const { profile, viewingCompanyId } = useAuth();
  const [items, setItems] = useState([]);
  const [handoffs, setHandoffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    loadData();
  }, [profile?.id, viewingCompanyId]);

  const loadData = async () => {
    if (!profile?.id) return;
    setLoading(true);
    const companyId = viewingCompanyId || profile.company_id;
    const [itemsRes, handoffRes] = await Promise.all([
      getDeliverableItemsForUser(profile.id, { companyId }),
      getHandoffDeliverablesForUser(profile.id, { companyId }),
    ]);
    setItems(itemsRes.data || []);
    setHandoffs(handoffRes.data || []);
    setLoading(false);
  };

  const handleToggle = async (item) => {
    const newCompleted = !item.is_completed;
    const { data } = await updateDeliverableItem(item.id, { is_completed: newCompleted });
    if (data) {
      setItems((prev) =>
        prev.map((i) =>
          i.id === data.id ? { ...i, is_completed: data.is_completed, completed_at: data.completed_at } : i
        )
      );
    }
  };

  const activeItems = items.filter((i) => !i.is_completed);
  const doneItems = items.filter((i) => i.is_completed);

  if (loading) return null;
  if (items.length === 0 && handoffs.length === 0) return null;

  // Group ALL items (active first, then today-completed) by project > phase > deliverable
  const sortedItems = [...activeItems, ...doneItems];
  const grouped = {};
  sortedItems.forEach((item) => {
    const deliverable = item.deliverables || {};
    const phase = deliverable.phases || {};
    const project = phase.projects || {};
    const key = `${phase.project_id}-${deliverable.id}`;
    if (!grouped[key]) {
      grouped[key] = {
        projectName: project.name || '未知專案',
        projectId: phase.project_id,
        projectColor: project.color || '#667eea',
        phaseName: phase.name || '未知階段',
        deliverableTitle: deliverable.title || '未知頁面',
        items: [],
      };
    }
    grouped[key].items.push(item);
  });

  return (
    <div className="my-deliverables">
      <div className="md-header" onClick={() => setCollapsed(!collapsed)}>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`md-toggle ${collapsed ? '' : 'open'}`}
        >
          <path d="M3 1l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="md-title">我的交付項目</span>
        <span className="md-count">{activeItems.length} 待完成</span>
        {doneItems.length > 0 && (
          <span className="md-done-count">{doneItems.length} 今日完成</span>
        )}
      </div>

      {!collapsed && (
        <div className="md-body">
          {/* 交棒待處理 */}
          {handoffs.length > 0 && (
            <div className="md-handoff-section">
              <div className="md-handoff-label">交棒待處理</div>
              {handoffs.map((h) => {
                const phase = h.phases || {};
                const project = phase.projects || {};
                return (
                  <div
                    key={h.id}
                    className="md-handoff-item"
                    onClick={() => onNavigate?.('project-phases', phase.project_id)}
                  >
                    <span className="md-project-dot" style={{ backgroundColor: project.color || '#667eea' }} />
                    <span className="md-handoff-path">
                      {project.name || '未知專案'} &gt; {phase.name || ''} &gt; {h.title}
                    </span>
                    {h.qa_status === 'fail' && <span className="md-handoff-fail-badge">QA Fail</span>}
                  </div>
                );
              })}
            </div>
          )}

          {Object.entries(grouped).map(([key, group]) => (
            <div key={key} className="md-group">
              <div className="md-group-header">
                <span
                  className="md-project-dot"
                  style={{ backgroundColor: group.projectColor }}
                />
                <span
                  className="md-project-name"
                  onClick={() => onNavigate?.('project-phases', group.projectId)}
                >
                  {group.projectName}
                </span>
                <span className="md-separator">&gt;</span>
                <span className="md-phase-name">{group.phaseName}</span>
                <span className="md-separator">&gt;</span>
                <span className="md-deliverable-name">{group.deliverableTitle}</span>
              </div>
              {group.items.map((item) => {
                const roleColor = ROLE_COLORS[item.role_type] || '#94a3b8';
                return (
                  <div key={item.id} className={`md-item ${item.is_completed ? 'completed' : ''}`}>
                    <button className="md-item-check" onClick={() => handleToggle(item)}>
                      {item.is_completed ? (
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                          <rect x="1" y="1" width="11" height="11" rx="3" fill="#10b981" />
                          <path d="M4 6.5l2 2 3.5-3.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                          <rect x="1" y="1" width="11" height="11" rx="3" stroke="#ccc" strokeWidth="1.5" />
                        </svg>
                      )}
                    </button>
                    <span
                      className="md-role-badge"
                      style={{ color: roleColor, borderColor: roleColor }}
                    >
                      {item.role_type}
                    </span>
                    <span className="md-item-title">{item.title}</span>
                    {item.estimated_hours > 0 && (
                      <span className="md-item-hours">{item.estimated_hours}h</span>
                    )}
                    {item.actual_hours > 0 && (
                      <span className="md-item-hours md-actual-hours">實際 {item.actual_hours}h</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyDeliverables;
